/**
 * Tender landing + BOQ Lead access from the login API `plans` field (snapshot at login).
 * Semantics: empty array ⇒ user HAS access; one or more entries ⇒ user does NOT have access.
 *
 * Uses `user.loginPlans` (same semantics as login `plans`). It is kept in sync with
 * GET /utils/user-active-plan-details/ whenever plan details are loaded (`loadPlanDetails` / `refreshUserPlans`).
 * Legacy sessions without `loginPlans`: treat as having access until the next successful plan fetch.
 *
 * @param {boolean} isAuthenticated
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function hasTenderBoqLeadAccessFromLoginPlans(isAuthenticated, user) {
  if (!isAuthenticated || !user || typeof user !== "object") return false;
  if (!Array.isArray(user.loginPlans)) {
    return true;
  }
  return user.loginPlans.length === 0;
}

/**
 * Header/footer: show Tender + BOQ Lead for guests; when logged in, same rule as login plans.
 * While auth is hydrating, show links to avoid flicker for logged-out visitors.
 *
 * @param {boolean} isAuthenticated
 * @param {object|null|undefined} user
 * @param {boolean} authLoading
 */
export function shouldShowTenderBoqLeadNavAndFooter(
  isAuthenticated,
  user,
  authLoading
) {
  if (authLoading) return true;
  if (!isAuthenticated) return true;
  return hasTenderBoqLeadAccessFromLoginPlans(isAuthenticated, user);
}

/**
 * @param {object} item - Header / menu item from menuItems
 * @param {boolean} hasTenderBoqLoginAccess - from hasTenderBoqLeadAccessFromLoginPlans
 */
export function isMainNavTenderOrBoqLeadItem(item) {
  if (!item || typeof item !== "object") return false;
  const isTenderLandingNav = item.name === "" && item.label === "Tender";
  const isBoqLeadNav = item.name === "boq-lead";
  return isTenderLandingNav || isBoqLeadNav;
}

/**
 * @param {Array<object>} items
 * @param {boolean} showTenderBoqItems - from shouldShowTenderBoqLeadNavAndFooter
 */
export function filterMainNavItemsForLoginPlans(items, showTenderBoqItems) {
  if (!Array.isArray(items)) return [];
  return items.filter(
    (item) => !isMainNavTenderOrBoqLeadItem(item) || showTenderBoqItems
  );
}
